import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  addRemark,
  callToLeadNumber,
  fetchAllBDMUsers,
  fetchBDMBoard,
  fetchDistrictCounts,
  fetchLogsCount,
  fetchRemarks,
  fetchTelecallerLead,
  fetchTelecallersForManager,
  findLeadByNumber,
  addCallLog,
  searchLeads,
  updateTelecallerLead,
  updateTelecallerLocations,
  getCallLogs,
} from "../../api/telecaller.api";
import { RootState } from "../../store";
import { ICallInfo, ICallLog } from "../../models/Call.model";
import { getUserId } from "../../utils/auth";
import ILeadData, { IRemark, ITelecaller } from "../../models/LeadData.model";
const getCallLogsKey = () => "call-logs" + getUserId();

interface TelecallerLeadState {
  currentLead: ILeadData | null;
  loadingList: boolean;
  loadingBoard: boolean;
  error: string | null;
  userLogsCount: number;
  logsByUser: Array<{
    userId: string;
    username: string;
    count: number;
    unpickedCount: number;
    totalDuration?: number;
  }>;
  districtsByUser: Record<string, { district: string; count: number }[]>;
  loadingDistrictsFor?: string;
  list: ITelecaller[];
  updatingLocations: boolean;
  remarks: IRemark[];
  searchResults?: any[];
  callLogs: ICallLog[];
  isTeleCallerCallsToday: boolean;
  todayDateForTelecaller: string;
  loadingAndErrorState: {
    fetchingTelecallerLead: boolean;
    updatingTelecallerLeadData: boolean;
    gettingLogsCount: boolean;
    gettingTelecallerUser: boolean;
    error: string | null;
    addingCallRemarks: boolean;
    addingAutoRemark?: boolean;
    callingtoLead?: boolean;
    getRemarks: boolean;
    addingRemarks: boolean;
    searchingLeads: boolean;
    gettingBdmBoardData: boolean;
    findingLeadByPhoneNumber: boolean;
    fetchingCallLogs: boolean;
  };
  shouldAddCallLog: boolean;
}
let callLogs: ICallLog[] = [];

const existingData = localStorage.getItem(getCallLogsKey());
if (existingData) callLogs = JSON.parse(existingData);

const initialState: TelecallerLeadState = {
  currentLead: null,
  error: null,
  userLogsCount: 0,
  logsByUser: [],
  districtsByUser: {},
  list: [],
  updatingLocations: false,
  remarks: [],
  searchResults: [],
  loadingList: false,
  loadingBoard: false,
  callLogs: callLogs,
  isTeleCallerCallsToday: false,
  todayDateForTelecaller: "",
  loadingAndErrorState: {
    fetchingTelecallerLead: false,
    gettingTelecallerUser: false,
    updatingTelecallerLeadData: false,
    gettingLogsCount: false,
    error: null,
    addingCallRemarks: false,
    addingAutoRemark: false,
    callingtoLead: false,
    getRemarks: false,
    addingRemarks: false,
    searchingLeads: false,
    gettingBdmBoardData: false,
    findingLeadByPhoneNumber: false,
    fetchingCallLogs: false,
  },
  shouldAddCallLog: false,
};

export const callFromWeb = createAsyncThunk(
  "telecaller/call",
  async (data: ICallInfo) => {
    const response = await callToLeadNumber(data);
    return response.data;
  }
);

const extractErrorMessage = (error: any): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "No Leads Available"
  );
};

export const getTelecallerLead = createAsyncThunk<
  any,
  void,
  { state: RootState; rejectValue: string }
>("telecallerLead/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchTelecallerLead();
    if (response === null) return null;
    return response.data;
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error));
  }
});

export const searchTelecallerLeads = createAsyncThunk(
  "telecallerLead/search",
  async (
    { query, userId }: { query: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const leads = await searchLeads(query, userId);
      return leads;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to search leads");
    }
  }
);

export const updateTelecallerLeadData = createAsyncThunk<
  any,
  { leadId: string; data: any; callLogData?: ICallLog[] },
  { state: RootState; rejectValue: string }
>(
  "telecallerLead/update",
  async ({ leadId, data, callLogData }, { rejectWithValue }) => {
    try {
      const response = await updateTelecallerLead(leadId, data, callLogData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const updateAndFetchNextLead = createAsyncThunk<
  any,
  { leadId: string; data: any },
  { state: RootState; rejectValue: string }
>("telecallerLead/updateAndFetchNext", async ({ leadId, data }, thunkAPI) => {
  const { dispatch, rejectWithValue } = thunkAPI;

  try {
    await dispatch(updateTelecallerLeadData({ leadId, data })).unwrap();

    dispatch(getLogsCount({}));
    const result = await dispatch(getTelecallerLead()).unwrap();

    if (result === null) {
      return null;
    }
    return result;
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error));
  }
});
export const getRemarks = createAsyncThunk(
  "leads/getRemarks",
  async (leadId: string, { rejectWithValue }) => {
    try {
      const res = await fetchRemarks(leadId);
      return { leadId, remarks: res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to load remarks");
    }
  }
);

export const addRemarks = createAsyncThunk(
  "leads/addRemarks",
  async (
    {
      leadId,
      text,
      callId,
      setFollowUp,
    }: { leadId: string; text: string; callId?: string; setFollowUp?: boolean },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const res = await addRemark(leadId, text, callId, setFollowUp);
      dispatch(getRemarks(leadId));
      return { leadId, remarks: res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add remark");
    }
  }
);

export const getLogsCount = createAsyncThunk<
  {
    byUser: Array<{
      userId: string;
      username: string;
      count: number;
      unpickedCount: number;
    }>;
    userCount: number;
  },
  { startDate?: string; endDate?: string },
  { state: RootState; rejectValue: string }
>(
  "telecallerLead/getLogsCount",
  async ({ startDate, endDate }, { rejectWithValue, getState }) => {
    try {
      const managerId = getUserId();
      if (!managerId) {
        return rejectWithValue("No manager logged in");
      }

      const data = await fetchLogsCount(managerId, startDate, endDate);

      // find the current manager’s personal count
      const userCount =
        data.byUser.find((u) => u.userId === managerId)?.count || 0;
      return { byUser: data.byUser, userCount };
    } catch (err: any) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);

export const getDistrictCounts = createAsyncThunk<
  { userId: string; districts: { district: string; count: number }[] },
  { userId: string; date?: string },
  { rejectValue: string }
>(
  "telecallerLead/getDistrictCounts",
  async ({ userId, date }, { rejectWithValue }) => {
    try {
      const districts = await fetchDistrictCounts(userId, date);
      return { userId, districts };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getTelecallerUser = createAsyncThunk<
  ITelecaller[],
  string,
  { state: RootState; rejectValue: string }
>("telecallers/load", async (managerId, { rejectWithValue }) => {
  try {
    return await fetchTelecallersForManager(managerId);
  } catch (err: any) {
    return rejectWithValue(err.message || "Unable to load telecallers");
  }
});

export const updateTelecallerLocation = createAsyncThunk<
  { userId: string; states: string[]; districts: string[] },
  { userId: string; states: string[]; districts: string[] },
  { rejectValue: string }
>(
  "telecaller/updateLocations",
  async ({ userId, states, districts }, { rejectWithValue, dispatch }) => {
    try {
      await updateTelecallerLocations(userId, {
        states,
        districts,
      });
      return { userId, states, districts };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update locations"
      );
    }
  }
);

export const getAllBDMs = createAsyncThunk<
  Array<{ _id: string; username: string; email: string; leadLogCount: number }>,
  { startDate?: string; endDate?: string },
  { rejectValue: string }
>("bdm/getAll", async ({ startDate, endDate }, { rejectWithValue }) => {
  try {
    const response = await fetchAllBDMUsers(startDate, endDate);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to fetch BDMs");
  }
});

export const getBDMBoardData = createAsyncThunk<
  {
    byUser: Array<{
      userId: string;
      username: string;
      count: number;
      unpickedCount: number;
    }>;
  },
  { bdmId: string; startDate?: string; endDate?: string },
  { rejectValue: string }
>(
  "bdm/getBoard",
  async ({ bdmId, startDate, endDate }, { rejectWithValue }) => {
    try {
      return await fetchBDMBoard(bdmId, startDate, endDate);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch BDM board");
    }
  }
);

export const findLeadByPhoneNumber = createAsyncThunk(
  "telecallerLead/findLeadByPhoneNumber",
  async (phoneNumber: string, { rejectWithValue }) => {
    try {
      const response = await findLeadByNumber(phoneNumber);
      const lead = response.data;

      return lead;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to find lead"
      );
    }
  }
);

export const addCallLogs = createAsyncThunk(
  "telecallerLead/saveCallLogs",
  async (
    {
      callLogs,
      leadId,
      showOnDateTelecaller,
    }: { callLogs: ICallLog[]; leadId: string; showOnDateTelecaller?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await addCallLog(callLogs, leadId, showOnDateTelecaller);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to save call logs");
    }
  }
);

export const fetchCallLogs = createAsyncThunk(
  "telecallerLead/fetchCallLogs",
  async (leadId: string, { rejectWithValue }) => {
    try {
      const logs = await getCallLogs(leadId);
      return { leadId, logs };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch call logs");
    }
  }
);

const telecallerLeadSlice = createSlice({
  name: "telecallerLead",
  initialState,
  reducers: {
    setCurrentLeadAndShouldAddCallLogTrue(state, action: PayloadAction<any>) {
      state.currentLead = action.payload;
      state.shouldAddCallLog = !!action.payload?._id;
      state.error = null;
    },
    setCurrentLead(state, action: PayloadAction<ILeadData | null>) {
      state.currentLead = action.payload;
    },

    checkTodaysCallLogs(state, action: PayloadAction<string>) {
      const today = new Date();
      const formattedToday = today
        .toLocaleDateString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/\//g, "-");

      const todaysCalls = state.callLogs.filter((log) => {
        return log.number === action.payload && log.callDate === formattedToday;
      });

      state.isTeleCallerCallsToday = todaysCalls.length > 0;
    },
    getTodayDateForTelecaller(state) {
      const today = new Date();
      const formattedDate = today
        .toLocaleDateString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/\//g, "-");

      state.todayDateForTelecaller = formattedDate;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTelecallerLead.pending, (state) => {
        state.loadingAndErrorState.fetchingTelecallerLead = true;
        state.loadingAndErrorState.error = null;
      })
      .addCase(getTelecallerLead.fulfilled, (state, action) => {
        state.loadingAndErrorState.fetchingTelecallerLead = false;
        state.currentLead = action.payload;
        state.loadingAndErrorState.error = null;
      })
      .addCase(getTelecallerLead.rejected, (state, action) => {
        state.loadingAndErrorState.fetchingTelecallerLead = false;
        state.loadingAndErrorState.error =
          action.payload || "Failed to fetch lead";
      })

      .addCase(updateTelecallerLeadData.pending, (state) => {
        state.loadingAndErrorState.updatingTelecallerLeadData = true;
        state.loadingAndErrorState.error = null;
      })
      .addCase(updateTelecallerLeadData.fulfilled, (state) => {
        state.loadingAndErrorState.updatingTelecallerLeadData = false;
      })
      .addCase(updateTelecallerLeadData.rejected, (state, action) => {
        state.loadingAndErrorState.updatingTelecallerLeadData = false;
        state.loadingAndErrorState.error =
          action.payload || "Failed to update lead";
      })

      .addCase(updateAndFetchNextLead.pending, (state) => {
        state.loadingAndErrorState.fetchingTelecallerLead = true;
        state.loadingAndErrorState.error = null;
      })
      .addCase(updateAndFetchNextLead.fulfilled, (state, action) => {
        state.loadingAndErrorState.fetchingTelecallerLead = false;
        state.currentLead = action.payload; // This will be null if no more leads
        state.loadingAndErrorState.error = null;
      })
      .addCase(updateAndFetchNextLead.rejected, (state, action) => {
        state.loadingAndErrorState.fetchingTelecallerLead = false;
        state.loadingAndErrorState.error =
          action.payload || "Failed to update and fetch next lead";
      })
      .addCase(getLogsCount.pending, (state) => {
        state.loadingAndErrorState.gettingLogsCount = true;
        state.loadingAndErrorState.error = null;
      })
      .addCase(getLogsCount.fulfilled, (state, action) => {
        state.loadingAndErrorState.gettingLogsCount = false;
        state.logsByUser = action.payload.byUser;
        state.userLogsCount = action.payload.userCount;
      })
      .addCase(getLogsCount.rejected, (state, action) => {
        state.loadingAndErrorState.gettingLogsCount = false;
        state.loadingAndErrorState.error =
          action.payload || "Failed to fetch logs count";
      })
      .addCase(getDistrictCounts.pending, (state, action) => {
        state.loadingDistrictsFor = action.meta.arg.userId;
      })
      .addCase(getDistrictCounts.fulfilled, (state, action) => {
        state.loadingDistrictsFor = undefined;
        const { userId, districts } = action.payload;
        state.districtsByUser[userId] = districts;
      })
      .addCase(getDistrictCounts.rejected, (state, action) => {
        state.loadingDistrictsFor = undefined;
        state.error = action.payload || "Failed to fetch district counts";
      })
      .addCase(getTelecallerUser.pending, (state) => {
        state.loadingAndErrorState.gettingTelecallerUser = true;
        state.error = null;
      })
      .addCase(getTelecallerUser.fulfilled, (state, action) => {
        state.loadingAndErrorState.gettingTelecallerUser = false;
        state.list = action.payload;
        state.error = null;
      })
      .addCase(getTelecallerUser.rejected, (state, action) => {
        state.loadingAndErrorState.gettingTelecallerUser = false;
        state.error = action.payload || "Failed to fetch telecallers";
      })
      .addCase(updateTelecallerLocation.pending, (state) => {
        state.updatingLocations = true;
      })
      .addCase(updateTelecallerLocation.fulfilled, (state, action) => {
        state.updatingLocations = false;
        const { userId, states, districts } = action.payload;
        const telecaller = state.list.find((t) => t._id === userId);
        if (telecaller) {
          telecaller.states = states;
          telecaller.districts = districts;
        }
      })
      .addCase(updateTelecallerLocation.rejected, (state, action) => {
        state.updatingLocations = false;
        state.error = action.payload || "Failed to update telecaller locations";
      })
      .addCase(getRemarks.pending, (state) => {
        state.loadingAndErrorState.getRemarks = true;
      })
      .addCase(getRemarks.fulfilled, (state, action) => {
        state.loadingAndErrorState.getRemarks = false;
        if (state.currentLead) {
          state.currentLead.remarks = action.payload.remarks;
        }
        state.remarks = action.payload.remarks;
      })
      .addCase(getRemarks.rejected, (state, action) => {
        state.loadingAndErrorState.getRemarks = false;
        state.error = action.payload as string;
      })

      .addCase(addRemarks.pending, (state) => {
        state.loadingAndErrorState.addingRemarks = true;
      })
      .addCase(addRemarks.fulfilled, (state, action) => {
        state.loadingAndErrorState.addingRemarks = false;
        state.remarks = action.payload.remarks;
      })
      .addCase(addRemarks.rejected, (state, action) => {
        state.loadingAndErrorState.addingRemarks = false;
        state.error = action.payload as string;
      })
      .addCase(searchTelecallerLeads.pending, (state) => {
        state.loadingAndErrorState.addingRemarks = true;
      })
      .addCase(searchTelecallerLeads.fulfilled, (state, action) => {
        state.loadingAndErrorState.searchingLeads = false;
        state.searchResults = action.payload;
      })
      .addCase(searchTelecallerLeads.rejected, (state, action) => {
        state.loadingAndErrorState.searchingLeads = false;
        state.error = action.payload as string;
      })
      .addCase(getAllBDMs.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAllBDMs.fulfilled, (state, action) => {
        state.loadingList = false;
        // Store BDM list in state.list
        state.list = action.payload.map((bdm) => ({
          _id: bdm._id,
          username: bdm.username,
          states: [],
          districts: [],
          leadLogCount: bdm.leadLogCount,
        }));
        state.error = null;
      })
      .addCase(getAllBDMs.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload || "Failed to fetch BDMs";
      })
      .addCase(getBDMBoardData.pending, (state) => {
        state.loadingBoard = true;
        state.error = null;
      })
      .addCase(getBDMBoardData.fulfilled, (state, action) => {
        state.loadingBoard = false;
        state.loadingAndErrorState.gettingBdmBoardData = false;
        // Store BDM board data in logsByUser o
        state.logsByUser = action.payload.byUser;
        state.error = null;
      })
      .addCase(getBDMBoardData.rejected, (state, action) => {
        state.loadingBoard = false;
        state.error = action.payload || "Failed to fetch BDM board";
      })
      .addCase(callFromWeb.pending, (state) => {
        state.loadingAndErrorState.callingtoLead = true;
        state.error = null;
      })
      .addCase(callFromWeb.fulfilled, (state) => {
        state.loadingAndErrorState.callingtoLead = false;
        state.error = null;
      })
      .addCase(callFromWeb.rejected, (state, action) => {
        state.loadingAndErrorState.callingtoLead = false;
      })
      .addCase(findLeadByPhoneNumber.pending, (state) => {
        state.loadingAndErrorState.findingLeadByPhoneNumber = true;
      })
      .addCase(findLeadByPhoneNumber.fulfilled, (state, action) => {
        state.loadingAndErrorState.findingLeadByPhoneNumber = false;

        if (action.payload && action.payload._id) {
          state.currentLead = action.payload;
        } else {
          state.shouldAddCallLog = false;
        }
        state.error = null;
      })

      .addCase(findLeadByPhoneNumber.rejected, (state, action) => {
        state.loadingAndErrorState.findingLeadByPhoneNumber = false;
      })
      .addCase(addCallLogs.pending, (state) => {
        state.loadingAndErrorState.addingCallRemarks = true;
      })
      .addCase(addCallLogs.fulfilled, (state) => {
        state.loadingAndErrorState.addingCallRemarks = false;
      })
      .addCase(addCallLogs.rejected, (state, action) => {
        state.loadingAndErrorState.addingCallRemarks = false;
        state.loadingAndErrorState.error = action.payload as string;
      })
      .addCase(fetchCallLogs.pending, (state) => {
        state.loadingAndErrorState.fetchingCallLogs = true;
      })
      .addCase(fetchCallLogs.fulfilled, (state, action) => {
        state.loadingAndErrorState.fetchingCallLogs = false;
        state.callLogs = action.payload.logs;
      })
      .addCase(fetchCallLogs.rejected, (state, action) => {
        state.loadingAndErrorState.fetchingCallLogs = false;
        state.loadingAndErrorState.error = action.payload as string;
      });
  },
});

export const {
  setCurrentLeadAndShouldAddCallLogTrue,
  checkTodaysCallLogs,
  setCurrentLead,
  getTodayDateForTelecaller,
} = telecallerLeadSlice.actions;
export default telecallerLeadSlice.reducer;
